/**
 * Settings > Service Types: WHM servers used for one-click cPanel login.
 * One row per server, matched to a Sonweb service's Web Server value by hostname. The API token is
 * write-only: it can be set or replaced here but is never shown again.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, NetworkCheck as TestIcon } from '@mui/icons-material';
import apiService from '../services/api';

interface HostingServer {
  id: string;
  hostname: string;
  label: string | null;
  whm_port: number;
  whm_username: string;
  token_set: boolean;
  verify_tls: boolean;
  is_active: boolean;
}

const apiMessage = (e: any, fallback: string) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const blank = { hostname: '', label: '', whm_port: '2087', whm_username: 'root', api_token: '', verify_tls: true, is_active: true };

const HostingServersSettings: React.FC = () => {
  const [servers, setServers] = useState<HostingServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; token_set: boolean } | null>(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setServers(await apiService.getHostingServers());
    } catch (e) {
      setError(apiMessage(e, 'Could not load hosting servers.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDialog = (s?: HostingServer) => {
    setError(null);
    setForm(s ? { hostname: s.hostname, label: s.label || '', whm_port: String(s.whm_port), whm_username: s.whm_username, api_token: '', verify_tls: s.verify_tls, is_active: s.is_active } : blank);
    setEditing({ id: s?.id ?? null, token_set: !!s?.token_set });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    const body = { ...form, whm_port: Number(form.whm_port) || 2087, api_token: form.api_token.trim() || undefined };
    try {
      if (editing.id) await apiService.updateHostingServer(editing.id, body);
      else await apiService.createHostingServer(body);
      setEditing(null);
      await load();
    } catch (e) {
      setError(apiMessage(e, 'Could not save the hosting server.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: HostingServer) => {
    if (!window.confirm(`Remove ${s.hostname}? cPanel login stops working for services on this server.`)) return;
    try {
      await apiService.deleteHostingServer(s.id);
      await load();
    } catch (e) {
      setNotice({ severity: 'error', text: apiMessage(e, 'Could not remove the server.') });
    }
  };

  const test = async (s: HostingServer) => {
    setTestingId(s.id);
    setNotice(null);
    try {
      const r = await apiService.testHostingServer(s.id);
      setNotice({ severity: r.ok ? 'success' : 'error', text: `${s.hostname}: ${r.message}` });
    } catch (e) {
      setNotice({ severity: 'error', text: apiMessage(e, 'Test failed.') });
    } finally {
      setTestingId(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h6">Hosting servers (cPanel login)</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>Add server</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        WHM servers SonOps can open cPanel on, for admins (on the service) and customers (in the customer portal). The
        hostname must match a Sonweb service's <strong>Web Server</strong> value, and the service's Panel Type must be
        cPanel. Create the WHM API token with the <strong>create-user-session</strong> privilege (WHM &rsaquo; Manage API Tokens).
      </Typography>
      {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}
      {error && !editing && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress size={24} />
      ) : servers.length === 0 ? (
        <Typography color="text.secondary">No hosting servers set up yet.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Hostname</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>WHM user : port</TableCell>
                <TableCell>API token</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {servers.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.hostname}</TableCell>
                  <TableCell>{s.label || '—'}</TableCell>
                  <TableCell>{s.whm_username} : {s.whm_port}</TableCell>
                  <TableCell>{s.token_set ? <Chip size="small" color="success" label="Set" /> : <Chip size="small" color="warning" label="Not set" />}</TableCell>
                  <TableCell>{s.is_active ? <Chip size="small" label="Active" /> : <Chip size="small" variant="outlined" label="Disabled" />}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" startIcon={testingId === s.id ? <CircularProgress size={14} /> : <TestIcon />} onClick={() => test(s)} disabled={testingId === s.id || !s.token_set}>Test</Button>
                    <IconButton size="small" onClick={() => openDialog(s)} aria-label={`Edit ${s.hostname}`}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => remove(s)} aria-label={`Remove ${s.hostname}`}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {editing && (
        <Dialog open onClose={() => !saving && setEditing(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{editing.id ? 'Edit hosting server' : 'Add hosting server'}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
            <TextField label="Hostname" required value={form.hostname} onChange={(e) => setForm({ ...form, hostname: e.target.value })}
              helperText="Exactly as it appears in the service's Web Server field, e.g. web01.sonicnetau.network" />
            <TextField label="Label (optional)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="WHM username" required value={form.whm_username} onChange={(e) => setForm({ ...form, whm_username: e.target.value })} sx={{ flex: 1 }} />
              <TextField label="WHM port" required value={form.whm_port} onChange={(e) => setForm({ ...form, whm_port: e.target.value.replace(/\D/g, '').slice(0, 5) })} sx={{ width: 140 }} inputProps={{ inputMode: 'numeric' }} />
            </Box>
            <TextField
              label={editing.token_set ? 'Replace API token (optional)' : 'API token'}
              type="password"
              autoComplete="new-password"
              value={form.api_token}
              onChange={(e) => setForm({ ...form, api_token: e.target.value })}
              helperText={editing.token_set ? 'A token is saved. Leave blank to keep it; it is never shown.' : 'Stored encrypted and never shown again.'}
            />
            <FormControlLabel control={<Checkbox checked={form.verify_tls} onChange={(e) => setForm({ ...form, verify_tls: e.target.checked })} />} label="Verify the server's TLS certificate (recommended)" />
            <FormControlLabel control={<Checkbox checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />} label="Active" />
            {error && <Alert severity="error">{error}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={saving || !form.hostname.trim()}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  );
};

export default HostingServersSettings;
